import React, { useState, useEffect } from 'react';
import { Button, Card } from '../ui';
import { useAssessment } from '../../context/AssessmentContext';
import { getAssessmentHistory, deleteAssessment } from '../../utils';
import { formatDate } from '../../utils';

export function HistoryView() {
  const { navigateToScreen, setResult } = useAssessment();
  const [assessments, setAssessments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = () => {
    try {
      const history = getAssessmentHistory();
      setAssessments(history);
    } catch (error) {
      console.error('Error loading history:', error);
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewResult = (assessment: any) => {
    setResult(assessment.result);
    navigateToScreen('results');
  };

  const handleDeleteAssessment = (assessmentId: string) => {
    if (window.confirm('Are you sure you want to delete this assessment?')) {
      try {
        deleteAssessment(assessmentId);
        loadHistory(); // Reload the list
      } catch (error) {
        console.error('Error deleting assessment:', error);
      }
    }
  };

  const getDoshaColor = (bodyType: string) => {
    if (bodyType.includes('vata')) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (bodyType.includes('pitta')) return 'text-orange-600 bg-orange-50 border-orange-200';
    if (bodyType.includes('kapha')) return 'text-green-600 bg-green-50 border-green-200';
    return 'text-gray-600 bg-gray-50 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neutral-800 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading your history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 fade-in">
          <h1 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-4">
            Your Assessment History
          </h1>
          <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
            Review your previous Ayurvedic assessments and track your wellness journey
          </p>
        </div>

        {/* History List */}
        {assessments.length === 0 ? (
          <Card className="text-center py-16 slide-up">
            <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-neutral-800 mb-2">No Assessments Yet</h3>
            <p className="text-neutral-600 mb-6">Complete your first assessment to see it here</p>
            <Button onClick={() => navigateToScreen('welcome')}>
              Start Assessment
            </Button>
          </Card>
        ) : (
          <div className="space-y-6 slide-up">
            {assessments.map((assessment, index) => (
              <Card key={assessment.id} className="hover:shadow-lg transition-shadow duration-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-3">
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getDoshaColor(assessment.result.primaryType)}`}>
                        {assessment.result.primaryType.replace('-', ' & ')}
                      </span>
                      <span className="ml-3 text-sm text-neutral-500">
                        {formatDate(assessment.timestamp)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-600">{assessment.result.percentages.vata.toFixed(1)}%</div>
                        <div className="text-xs text-neutral-500">Vata</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-orange-600">{assessment.result.percentages.pitta.toFixed(1)}%</div>
                        <div className="text-xs text-neutral-500">Pitta</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-bold text-green-600">{assessment.result.percentages.kapha.toFixed(1)}%</div>
                        <div className="text-xs text-neutral-500">Kapha</div>
                      </div>
                    </div>
                    
                    {assessment.notes && (
                      <p className="text-sm text-neutral-600">{assessment.notes}</p>
                    )}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2 mt-4 md:mt-0">
                    <Button
                      variant="secondary"
                      onClick={() => handleViewResult(assessment)}
                      className="text-sm"
                    >
                      View Details
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => handleDeleteAssessment(assessment.id)}
                      className="text-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="text-center mt-12 slide-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              onClick={() => navigateToScreen('welcome')}
              className="px-8 py-3"
            >
              ← Back to Home
            </Button>
            
            {assessments.length > 0 && (
              <Button
                onClick={() => navigateToScreen('quiz')}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                Take New Assessment
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

